import { getAccessCondition, getCartMinimumAmountEuros } from '@/components/rewards/utils/rewardAccessConditions'
import { getApiErrorMessage } from '@/utils/apiError'

import { parseConditions } from '@/components/rewards/utils/offerRewardHydration'
import type { RewardConditionPayload } from '@/components/rewards/utils/offerRewardPayload'
import { buildRewardPayload } from '@/components/rewards/utils/offerRewardPayload'
import {
  validateCartItemCount,
  validateMinAmount,
  validateReductionValue,
  validateXForYBuyQuantity,
  validateXForYFreeQuantity
} from '@/components/rewards/utils/offerRewardValidation'
import { getApplyReductionOn } from '@/components/rewards/utils/rewardFormInitialization'

import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'

import type { LoyaltyReward, Partner, PartnerParameter, RewardPayload } from '@/types/types'

import { getRewardVouchers } from '@/components/rewards/utils/rewardHelpers'
import {
  createLoyaltyProgramReward as createLoyaltyProgramRewardApi,
  createReward as createRewardApi,
  dissociateLoyaltyProgramReward as dissociateLoyaltyProgramRewardApi,
  fetchLoyaltyProgramRewardById as fetchLoyaltyProgramRewardByIdApi,
  fetchMerchantRewardById as fetchMerchantRewardByIdApi,
  fetchPartnerParameters,
  fetchRewardTypeIdsBySlug,
  updateLoyaltyProgramReward as updateLoyaltyProgramRewardApi,
  updateReward as updateRewardApi
} from '@/components/rewards/utils/rewardApi'
import { groupPartnerAccounts } from '@/utils/productHelpers'

export type OfferRewardModalEmit = {
  (e: 'close'): void
  (e: 'update:reward', reward: LoyaltyReward): void
}

export type OfferRewardTouchedFields = {
  cartItemCount: boolean
}

export type RewardFormErrorField =
  | 'reductionValue'
  | 'discountTargetProducts'
  | 'xForYBuyQuantity'
  | 'xForYFreeQuantity'
  | 'xForYItems'
  | 'minAmount'
  | 'requiredProducts'
  | 'cartItemCount'

const currentReward = ref<LoyaltyReward | null>(null)
const isLoading = ref(false)
const rewardTypeIdsBySlug = ref<Record<string, number> | null>(null)
const isRewardTypesLoading = ref(false)
const hasLoadedRewardTypes = ref(false)
const rewardTypesError = ref<string | null>(null)
const partnersParameters = ref<(Partner & { accounts: PartnerParameter[] })[]>([])
const isLoadingPartners = ref(false)

export function useRewardForm(emit?: OfferRewardModalEmit) {
  const { t } = useI18n()
  const { user } = useLocalStorage()
  const notification = useNotification()
  const { flags } = useFeatureFlag()

  const merchantId = computed(() => user.value.merchant.id)

  const fetchRewardTypes = async (): Promise<void> => {
    if (hasLoadedRewardTypes.value || isRewardTypesLoading.value) return
    try {
      rewardTypesError.value = null
      isRewardTypesLoading.value = true
      const map = await fetchRewardTypeIdsBySlug()
      const requiredSlugs = [
        'free_product',
        'voucher_discount_percent',
        'voucher_discount_amount',
        'voucher_x_for_y',
        'voucher_free_delivery'
      ]
      const missing = requiredSlugs.filter((s) => map[s] == null)
      if (missing.length > 0) {
        throw new Error(`Missing reward types: ${missing.join(', ')}`)
      }

      rewardTypeIdsBySlug.value = map
      hasLoadedRewardTypes.value = true
    } catch (e: unknown) {
      rewardTypeIdsBySlug.value = null
      hasLoadedRewardTypes.value = false
      rewardTypesError.value = e instanceof Error ? e.message : 'Unable to load reward types'
    } finally {
      isRewardTypesLoading.value = false
    }
  }

  void fetchRewardTypes()

  const rewardVouchers = computed(() => {
    if (!rewardTypeIdsBySlug.value) return []
    return getRewardVouchers(t, rewardTypeIdsBySlug.value)?.filter((v) =>
      flags.value['offer-engine']?.enabled ? true : v.slug !== 'voucher_x_for_y'
    )
  })

  const isRewardTypesReady = computed(() => rewardVouchers.value.length > 0)

  const defaultReward = computed<LoyaltyReward | null>(() => {
    const first = rewardVouchers.value[0]
    if (!first) return null
    return {
      id: null,
      points: null,
      claimLimitQuantity: null,
      claimLimitDays: null,
      name: '',
      description: '',
      externalId: '',
      pictureUrl: '',
      value: null,
      validityInDays: null,
      minimumOrderAmount: null,
      voucherDiscountAmount: null,
      discountValuePercent: null,
      storeIds: [],
      rewardType: {
        name: first.text,
        id: first.id,
        slug: first.slug,
        isVoucher: first.isVoucher
      },
      rewardTypeId: first.id,
      notificationTextOverride: '',

      lists: [],
      segments: [],
      includedSegments: [],
      excludedSegments: [],
      items: [],
      stores: [],
      hasReachedClaimLimit: false,
      usages: [],

      autopilots: [],
      isForSingleUse: false
    } as LoyaltyReward
  })

  const isOfferFormTouched = ref(false)

  const isDiscountType = computed(() => slugBelongsTo('discount', currentReward.value?.rewardType?.slug))
  const isXForYReward = computed(() => slugBelongsTo('xForY', currentReward.value?.rewardType?.slug))
  const isPercentDiscount = computed(() => slugBelongsTo('percentDiscount', currentReward.value?.rewardType?.slug))

  const hasMinAmountError = computed(() => {
    const reward = currentReward.value
    if (!reward || getAccessCondition(reward) !== 'min_amount') return false
    return !validateMinAmount(getCartMinimumAmountEuros(reward))
  })

  const hasCartItemCountError = computed(() => {
    const reward = currentReward.value
    if (!reward || getAccessCondition(reward) !== 'cart_item_count') return false
    return !validateCartItemCount(reward.cartItemCount ?? null)
  })

  const hasXForYBuyQuantityError = computed(() => {
    if (!isXForYReward.value || !currentReward.value) return false
    return !validateXForYBuyQuantity(currentReward.value.value)
  })

  const hasXForYFreeQuantityError = computed(() => {
    if (!isXForYReward.value || !currentReward.value) return false
    return !validateXForYFreeQuantity(currentReward.value.voucherDiscountAmount)
  })

  const hasXForYQuantityError = computed(() => hasXForYBuyQuantityError.value || hasXForYFreeQuantityError.value)

  const hasXForYItemsError = computed(() => {
    if (!isXForYReward.value || !currentReward.value) return false
    return (currentReward.value.items?.length ?? 0) === 0
  })

  const hasReductionValueError = computed(() => {
    if (!isDiscountType.value || !currentReward.value) return false
    return !validateReductionValue(currentReward.value.value, isPercentDiscount.value)
  })

  const hasDiscountTargetProductsError = computed(() => {
    if (!isDiscountType.value || !currentReward.value) return false
    if (getApplyReductionOn(currentReward.value) !== 'products') return false
    return (currentReward.value.items?.length ?? 0) === 0
  })

  const hasRequiredProductsError = computed(() => {
    const reward = currentReward.value
    if (!reward || getAccessCondition(reward) !== 'products_required') return false
    return (reward.requiredProductExternalIds ?? []).length === 0
  })

  const hasRewardFormErrors = computed(() => {
    if (!currentReward.value) return false
    if (hasReductionValueError.value) return true
    if (hasCartItemCountError.value) return true
    if (hasMinAmountError.value) return true
    if (isXForYReward.value && (hasXForYQuantityError.value || hasXForYItemsError.value)) return true
    if (hasDiscountTargetProductsError.value) return true
    if (hasRequiredProductsError.value) return true
    return false
  })

  const firstRewardFormError = computed((): RewardFormErrorField | null => {
    const reward = currentReward.value
    if (!reward) return null
    if (hasReductionValueError.value) return 'reductionValue'
    if (hasDiscountTargetProductsError.value) return 'discountTargetProducts'
    if (isXForYReward.value) {
      if (hasXForYBuyQuantityError.value) return 'xForYBuyQuantity'
      if (hasXForYFreeQuantityError.value) return 'xForYFreeQuantity'
      if (hasXForYItemsError.value) return 'xForYItems'
    }
    if (hasMinAmountError.value) return 'minAmount'
    if (hasRequiredProductsError.value) return 'requiredProducts'
    if (hasCartItemCountError.value) return 'cartItemCount'
    return null
  })

  const hasOtherUsageThanOffer = computed(() => {
    const reward = currentReward.value
    if (!reward || !Array.isArray(reward.usages) || reward.usages.length === 0) return false
    return reward.usages.some((usage) => usage !== 'offer')
  })

  const hasCompatibleIntegration = computed(() => {
    return partnersParameters.value.some(
      (partner) => partner.accounts.some((account) => account.isActive) && partner.hasRewardCartSupport
    )
  })

  const productCatalogPartners = computed(() => partnersParameters.value.filter((partner) => partner.hasRewardSupport))

  const disabledReason = computed(() => {
    if (hasOtherUsageThanOffer.value) return 'hasOtherUsageThanOffer'
    if (!hasCompatibleIntegration.value) return 'hasCompatibleIntegration'
    return null
  })
  const isProductFeatureDisabled = computed(() => hasOtherUsageThanOffer.value || !hasCompatibleIntegration.value)

  const notifyFetchRewardError = () => {
    notification.create('error', {
      text: t('pages.reward.errors.fetchReward')
    })
  }

  const fetchLoyaltyProgramRewardById = async (
    rewardId: number,
    handleExternalIdAsItems = false
  ): Promise<LoyaltyReward | null> => {
    try {
      return await fetchLoyaltyProgramRewardByIdApi(merchantId.value, rewardId, handleExternalIdAsItems)
    } catch (error: unknown) {
      notifyFetchRewardError()
      throw error
    }
  }

  const fetchMerchantRewardById = async (
    rewardId: number,
    handleExternalIdAsItems = false
  ): Promise<LoyaltyReward | null> => {
    try {
      return await fetchMerchantRewardByIdApi(merchantId.value, rewardId, handleExternalIdAsItems)
    } catch (error: unknown) {
      notifyFetchRewardError()
      throw error
    }
  }

  const dissociateLoyaltyProgramReward = async (rewardId: number): Promise<void> => {
    await dissociateLoyaltyProgramRewardApi(merchantId.value, rewardId)
  }

  const loadLoyaltyProgramRewardById = async (rewardId: number) => {
    try {
      isLoading.value = true

      currentReward.value = await fetchLoyaltyProgramRewardById(rewardId, flags.value['offer-engine']?.enabled)

      return currentReward.value
    } catch {
      currentReward.value = null
      return null
    } finally {
      isLoading.value = false
    }
  }

  const loadRewardById = async (rewardId: number) => {
    try {
      isLoading.value = true

      currentReward.value = await fetchMerchantRewardById(rewardId, flags.value['offer-engine']?.enabled)

      return currentReward.value
    } catch {
      currentReward.value = null
      return null
    } finally {
      isLoading.value = false
    }
  }

  const saveReward = async (payload: RewardPayload) => {
    const rewardId = payload.id

    if (rewardId) {
      return await updateRewardApi(merchantId.value, rewardId, payload, flags.value['offer-engine']?.enabled)
    }
    return await createRewardApi(merchantId.value, payload, flags.value['offer-engine']?.enabled)
  }

  const saveLoyaltyProgramReward = async (payload: RewardPayload) => {
    const rewardId = payload.id

    if (rewardId) {
      return await updateLoyaltyProgramRewardApi(
        merchantId.value,
        rewardId,
        payload,
        flags.value['offer-engine']?.enabled
      )
    }
    return await createLoyaltyProgramRewardApi(merchantId.value, payload, flags.value['offer-engine']?.enabled)
  }

  const buildPayload = (reward: LoyaltyReward) => {
    const sourceReward = currentReward.value ?? reward
    const accessCondition = getAccessCondition(sourceReward)
    const parsed =
      accessCondition === 'products_required'
        ? parseConditions((sourceReward.conditions ?? []) as RewardConditionPayload[])
        : null

    return buildRewardPayload(sourceReward, {
      isXForY: isXForYReward.value,
      accessCondition,
      cartItemCount: sourceReward.cartItemCount ?? null,
      requiredProducts: parsed?.requiredProducts ?? [],
      requiredProductQuantities: parsed?.requiredProductQuantities ?? {},
      requiredProductsOperator: parsed?.requiredProductsOperator ?? 'contains_all',
      applyReductionOn: getApplyReductionOn(sourceReward),
      rewardTypeIdsBySlug: rewardTypeIdsBySlug.value
    })
  }

  const handleRewardUpdate = async (reward: LoyaltyReward) => {
    if (!reward) {
      notification.create('error', {
        text: t('pages.reward.errors.rewardNotFound')
      })
      return
    }

    const payload = buildPayload(reward)
    if (!payload) return

    try {
      const savedReward = await saveReward(payload as RewardPayload)

      if (reward.id) {
        notification.create('success', {
          text: t('pages.reward.editReward.success')
        })
      } else {
        notification.create('success', {
          text: t('pages.reward.createReward.success')
        })
      }

      emit?.('update:reward', savedReward)
      emit?.('close')
    } catch (error: unknown) {
      notification.create('error', {
        text: getApiErrorMessage(error) ?? t('pages.reward.editReward.error')
      })
    }
  }

  const loadPartnerParameters = async () => {
    try {
      isLoadingPartners.value = true
      const data = await fetchPartnerParameters()
      partnersParameters.value = groupPartnerAccounts(data)
    } catch {
      partnersParameters.value = []
    } finally {
      isLoadingPartners.value = false
    }
  }

  return {
    currentReward,
    isLoading,
    loadRewardById,
    loadLoyaltyProgramRewardById,
    rewardVouchers,
    fetchRewardTypes,
    isRewardTypesReady,
    rewardTypesError,
    rewardTypeIdsBySlug,
    defaultReward,
    saveReward,
    saveLoyaltyProgramReward,
    dissociateLoyaltyProgramReward,
    isOfferFormTouched,
    hasRewardFormErrors,
    firstRewardFormError,
    hasMinAmountError,
    hasOtherUsageThanOffer,
    isProductFeatureDisabled,
    disabledReason,
    handleRewardUpdate,
    partnersParameters,
    productCatalogPartners,
    isLoadingPartners,
    loadPartnerParameters
  }
}
